import { assembliesApi } from '@/api/assemblies.api';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MOTION } from '@/components/common/motion';
import { useToolSystem } from '@/context/ToolSystemContext';
import type { ToolImportReport } from '@/types/toolImport.types';
import { extractDroppedFiles } from '@/utils/dropFiles';
import { UploadSimple } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/** Suffixes the backend accepts for a tool-database export. */
const ACCEPTED_EXTENSIONS = ['.xml', '.omtdx'];

/**
 * The two import modes, in the order they appear in the segmented control, expressed as the
 * `retire_missing` value each one sends: "Doplnit" (false) then "Nahradit" (true).
 */
const IMPORT_MODES = [false, true] as const;

/** Why one import run failed, so the component can pick the message without re-reading axios internals. */
type ImportFailure =
  | { kind: 'invalidFile'; detail: string }
  | { kind: 'conflict'; detail: string }
  | { kind: 'unauthorized' }
  | { kind: 'server' }
  | { kind: 'network' }
  | { kind: 'timeout' }
  | { kind: 'unknown'; detail?: string };

/** Network-layer codes that mean "the request never reached a usable backend". */
const NETWORK_ERROR_CODES = ['ECONNREFUSED', 'ENOTFOUND', 'ERR_NETWORK'];

const isToolExportFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
};

/**
 * Sort an import rejection into one of the cases the UI has words for.
 *
 * Two shapes arrive here: an `AxiosError` carrying the backend `detail`, and the plain
 * `Error` the response interceptor in `api/client.ts` substitutes for timeouts.
 */
function classifyImportError(error: unknown): ImportFailure {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { status?: number; data?: { detail?: string } };
      code?: string;
    };
    const status = axiosError.response?.status;
    const detail = axiosError.response?.data?.detail;

    if (axiosError.code === 'ECONNABORTED') return { kind: 'timeout' };
    if (axiosError.code && NETWORK_ERROR_CODES.includes(axiosError.code)) return { kind: 'network' };
    if (status === 400) return { kind: 'invalidFile', detail: detail ?? '' };
    if (status === 409) return { kind: 'conflict', detail: detail ?? '' };
    if (status === 401 || status === 403) return { kind: 'unauthorized' };
    if (status != null && status >= 500) return { kind: 'server' };
    if (detail) return { kind: 'unknown', detail };
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes('timeout') || message.includes('took too long')) return { kind: 'timeout' };
    if (message.includes('network') || message.includes('connection')) return { kind: 'network' };
  }

  return { kind: 'unknown' };
}

interface ToolImportDropZoneProps {
  /** Called with the backend report once an import finished successfully. */
  onImported: (report: ToolImportReport) => void;
  className?: string;
}

/**
 * Drop target that fills the tool database from a tool-database export.
 *
 * Takes exactly one `.xml` / `.omtdx` file — by drop or by file picker — posts it to
 * `POST /assemblies/load-xml` and hands the resulting report to the parent, which owns
 * both the success toast and the report dialog. Failures stay here: the zone knows what
 * went wrong and says so, the parent only ever hears about imports that worked.
 *
 * The import-mode switch rests on "Doplnit" (add). The backend defaults to retiring, which
 * is right for a full catalogue export but destructive for a partial one, so the safer of
 * the two modes is the one the user gets without deciding anything.
 *
 * Laid out vertically, because import is a rare action that now lives inside a dialog rather
 * than in a full-width strip above the table. What the two modes do is spelled out underneath
 * them as plain text: the choice is destructive in one direction, so the explanation should
 * not be hidden behind a "?" the user has to think to press.
 */
export const ToolImportDropZone: FC<ToolImportDropZoneProps> = ({ onImported, className = '' }) => {
  const { t } = useTranslation();
  const { toolSystem } = useToolSystem();
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [retireMissing, setRetireMissing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modeButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  /**
   * Arrow keys move the selection, as the radiogroup pattern expects; with two options either
   * direction simply lands on the other one. Focus follows the selection so the roving
   * tabindex stays on the checked option.
   */
  const handleModeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isImporting) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowUp' && e.key !== 'ArrowRight' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const delta = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1;
    const current = IMPORT_MODES.indexOf(retireMissing);
    const next = (current + delta + IMPORT_MODES.length) % IMPORT_MODES.length;
    setRetireMissing(IMPORT_MODES[next]);
    modeButtonRefs.current[next]?.focus();
  };

  const failureMessage = (failure: ImportFailure): string => {
    switch (failure.kind) {
      case 'invalidFile':
        return failure.detail
          ? t('toolManagement.importInvalidFile', { detail: failure.detail })
          : t('toolManagement.importFailed');
      case 'conflict':
        return failure.detail || t('toolManagement.importConflict');
      case 'unauthorized':
        return t('toolManagement.importUnauthorized');
      case 'server':
        return t('toolManagement.importServerError');
      case 'network':
        return t('toolManagement.importNetworkError');
      case 'timeout':
        return t('toolManagement.importTimeout');
      default:
        return failure.detail || t('toolManagement.importFailed');
    }
  };

  const runImport = async (file: File) => {
    setIsImporting(true);
    try {
      const report = await assembliesApi.importXml(file, { retireMissing, toolSystem });
      onImported(report);
    } catch (error) {
      console.error('Tool database import failed:', error);
      toast.error(failureMessage(classifyImportError(error)));
    } finally {
      setIsImporting(false);
      // Let the same file be picked again after a failed run.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Accept a candidate set from either input path; returns the single file to import. */
  const pickSingleExport = (files: File[]): File | null => {
    const exports = files.filter(isToolExportFile);
    if (exports.length === 0) {
      toast.error(t('validation.toolExportFileRequired'));
      return null;
    }
    if (exports.length > 1) {
      toast.error(t('validation.toolExportSingleFile'));
      return null;
    }
    return exports[0];
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isImporting) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isImporting) return;

    const { files, hadDirectory, receivedAnything } = extractDroppedFiles(e.dataTransfer);
    if (files.length === 0) {
      if (hadDirectory) {
        toast.error(t('validation.dropFolderToolExport'));
      } else if (!receivedAnything) {
        toast.error(t('validation.dropNoFileReceived'));
      } else {
        toast.error(t('validation.toolExportFileRequired'));
      }
      return;
    }

    const file = pickSingleExport(files);
    if (file) await runImport(file);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;

    const file = pickSingleExport(Array.from(selected));
    if (file) {
      await runImport(file);
      return;
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const dropAreaClass = isDragging
    ? 'border-primary bg-primary/5'
    : 'border-gray-300 bg-white hover:border-primary';

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Nested radius: the outer dialog content has no card of its own, so the drop area is
          the outer box here and takes the 12 px step below a 16 px card. */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isImporting}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:cursor-progress ${dropAreaClass}`}
      >
        {isImporting ? (
          <LoadingSpinner size="small" />
        ) : (
          <UploadSimple size={32} className={isDragging ? 'text-primary' : 'text-gray-400'} aria-hidden="true" />
        )}
        <span className="block text-sm font-medium text-gray-900">
          {isImporting ? t('toolManagement.importInProgress') : t('toolManagement.importDropZoneTitle')}
        </span>
        <span className="block text-xs text-gray-500">
          {isImporting ? t('toolManagement.importInProgressHint') : t('toolManagement.importDropZoneHint')}
        </span>
      </button>

      <div className="flex flex-col gap-2">
        {/* Segmented pill: "Doplnit" (retire_missing=false) | "Nahradit" (true). */}
        <div
          role="radiogroup"
          aria-label={t('toolManagement.importModeAriaLabel')}
          onKeyDown={handleModeKeyDown}
          className="inline-flex w-full items-center gap-1 rounded-full bg-gray-100 p-1"
        >
          {IMPORT_MODES.map((mode, index) => {
            const isActive = retireMissing === mode;
            return (
              <button
                key={mode ? 'replace' : 'add'}
                ref={(el) => {
                  modeButtonRefs.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={isActive}
                tabIndex={isActive ? 0 : -1}
                disabled={isImporting}
                onClick={() => setRetireMissing(mode)}
                className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-progress ${
                  isActive ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {mode ? t('toolManagement.importModeReplace') : t('toolManagement.importModeAdd')}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-2 text-xs text-gray-500 [text-wrap:pretty]">
          <p className="m-0">{t('toolManagement.importModeHelpAdd')}</p>
          <p className="m-0">{t('toolManagement.importModeHelpReplace')}</p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xml,.omtdx"
        className="hidden"
        onChange={handleFileSelect}
        disabled={isImporting}
      />
    </div>
  );
};
